

import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { matchConfigurationData, matchDefaultConfigurationData } from "@/lib/utils/crm/matchConfigurationData";
import { Configuration } from "@/types/crm/configuration/configuration";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useLayoutEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { ParametersForm } from "@/features/crm/components/organisms/ParametersForm/ParametersForm";
import { Loader } from "lucide-react";
import { useUpdateCrmConfig } from "@/features/crm_config/hooks/useMutation/useUpdateCrmConfig";
import { BorderBox } from "@/components/atoms/BorderBox";
import { Link } from "react-router-dom";

interface CreateCrmConfigModalProps {
    showModal: boolean;
    setShowModal: () => void;
    crm: Tables<"crm">;
    crmProject: Tables<"crm_project">;

    crmConfigData: Tables<"crm_config"> | null;
}

export default function EditCrmConfigModal({
    showModal,
    setShowModal,
    crm,
    crmProject,
    crmConfigData,
}: CreateCrmConfigModalProps) {
    const schema = z.object({
        name: z.string().min(1, "Name is required"),
        description: z.string().optional(),
        parameter: z.array(z.object({
            parameter_id: z.string().optional().nullable(),
            value: z.string().optional().nullable(),
            name: z.string().optional().nullable(),
        })).optional().nullable(),
        default_parameter: z.array(z.object({
            parameter_id: z.string().optional().nullable(),
            value: z.string().optional().nullable(),
            name: z.string().optional().nullable(),
        })).optional().nullable(),
    });
    type schemaType = z.infer<typeof schema>;
    const form = useForm<schemaType>({ resolver: zodResolver(schema) });

    const { mutateAsync: updateCrmConfig, isPending } = useUpdateCrmConfig()
    const onSubmit = async (data: schemaType) => {
        console.log(data)
        if (!crm || !crmProject) return;

        const requestBody = {
            id: crmConfigData?.id,
            name: data.name,
            description: data.description,
            crm_id: crm.id,
            crm_project_id: crmProject.id,
            selected_configuration: {
                parameter: [
                    ...data.parameter?.map((parameter) => {
                        return {
                            id: parameter.parameter_id,
                            value: parameter.value,
                        }
                    }) || [],
                    ...data.default_parameter?.map((parameter) => {
                        return {
                            id: parameter.parameter_id,
                            value: parameter.value,
                        }
                    }) || [],
                ]
            },
        };

        updateCrmConfig(requestBody)
            .then(() => {
                SuccessPopup("Survey CRM configuration updated successfully")
                setShowModal();
            })
            .catch((err) => {
                console.log(err);
                ErrorPopup("Something went wrong");
            })
    };
    /* 
       * Handle CRM Parameters
       */
    const { fields: defaultParametersFields } = useFieldArray({
        control: form.control,
        name: 'default_parameter',
    });

    const { fields: parametersFields } = useFieldArray({
        control: form.control,
        name: 'parameter',
    });
    console.log("defaultParametersFields")
    console.log(defaultParametersFields)
    useEffect(() => {
        if (!crm || !crmProject) return
        form.reset({
            name: crmConfigData?.name || "",
            description: crmConfigData?.description || "",
            parameter: matchConfigurationData(crm, crm.configuration as Configuration)?.parameter?.survey?.map(p => {
                return {
                    parameter_id: p.id,
                    name: p.name,
                    value: crmConfigData?.selected_configuration?.parameter?.find(param => param.id === p.id)?.value || "",
                }
            }) || [],
            default_parameter: matchDefaultConfigurationData(crm, crmProject)?.parameter?.survey?.map(p => {
                return {
                    parameter_id: p.id,
                    name: p.name,
                    value: crmConfigData?.selected_configuration?.parameter?.find(param => param.id === p.id)?.value || "",
                }
            }) || [],
        })
    }, [showModal, form, crm, crmProject, crmConfigData])

    useLayoutEffect(() => {
        form.reset()
    }, [showModal, form])

    return (
        <Dialog open={showModal} onOpenChange={setShowModal} defaultOpen={false} >
            <DialogContent >
                <DialogHeader>
                    <DialogTitle className=''>
                        <div className="p-0 m-0 font-bold text-start">
                            Survey Configuration for CRM Integration
                        </div>
                    </DialogTitle>
                    <DialogDescription className="text-start">
                        Manage survey parameters to enhance customer feedback collection and streamline data processing in your CRM workflow.
                    </DialogDescription>

                </DialogHeader>
                <hr />
                <ScrollArea className="max-h-[60vh] sm:max-h-[70vh]">
                    <div className="flex flex-col gap-2 p-1 mb-3">
                        <Form {...form}>
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>CRM Config Name</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <ParametersForm
                                label="Default Parameters"
                                parametersFields={defaultParametersFields}
                                form={form}
                                defaultParameter
                                parametersObj={crm?.configuration_data?.parameter?.survey || {}}
                            />

                            <ParametersForm
                                label="Custom Parameters"
                                parametersFields={parametersFields}
                                form={form}
                                parametersObj={crm?.configuration_data?.parameter?.survey || {}}
                            />
                        </Form>
                        <BorderBox variant='info' size='sm'>
                            <span>
                                If you would like to add additional parameters to this configuration, <Link to={`/organization/${crm?.organisation_id}/manage-organization?crmId=${crm?.id}&secondaryTab=parameters`}><span className="font-bold text-nowrap">click here</span></Link>.
                            </span>
                        </BorderBox>
                    </div>
                </ScrollArea>
                <hr />
                <DialogFooter>
                    <div className="flex items-stretch justify-end m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                        <Button onClick={() => {
                            form.handleSubmit(onSubmit)()
                        }}
                            disabled={isPending || Object.keys(form?.formState.errors || {}).length > 0}
                        >
                            {isPending && <Loader className="w-4 h-4 animate-spin" />}
                            Save
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog >
    )
}
